// pages/Services.js — mobile responsive

import React, { useEffect, useState } from 'react';
import CtaBand from '../components/CtaBand';

const services = [
  { icon: '✏️', title: 'Bespoke Kitchen Design', text: "Every kitchen we create is unique. Our design team works closely with you to develop a layout and aesthetic that perfectly suits your space and lifestyle.", bullets: ['Personalised design consultation', 'Space planning & optimisation', '3D visualisation & renders'] },
  { icon: '📦', title: 'Custom Cabinetry',        text: "Handcrafted cabinetry tailored to your exact specifications. From traditional shaker to sleek handleless designs, we create furniture that lasts.",        bullets: ['Made-to-measure units', 'Premium wood & finish options', 'Soft-close mechanisms'] },
  { icon: '🗂️', title: 'Worktop Selection',       text: "The perfect worktop completes your kitchen. We offer an extensive range of materials including granite, quartz, marble, and solid wood.",                   bullets: ['Natural stone options', 'Engineered quartz', 'Precision templating & fitting'] },
  { icon: '💡', title: 'Lighting Design',          text: "Lighting transforms a kitchen. We design comprehensive lighting schemes that combine task, ambient, and accent lighting for the perfect atmosphere.",         bullets: ['Task & ambient lighting', 'Under-cabinet LED systems', 'Smart lighting integration'] },
  { icon: '🔌', title: 'Appliance Integration',    text: "We help you select and seamlessly integrate premium appliances from leading brands, ensuring they complement your design perfectly.",                         bullets: ['Appliance specification', 'Seamless integration', 'Smart home compatibility'] },
  { icon: '🔧', title: 'Full Installation',         text: "Our skilled installation team handles every aspect of your project, from demolition to final finishing touches, with minimal disruption to your home.",       bullets: ['Project management', 'Full demolition & fit-out', 'Aftercare & warranty'] },
];

const brands = ['Siemens', 'Miele', 'Bosch', 'NEFF', 'Samsung', 'Hafele'];

export default function Services() {
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <div className="page-wrapper--inner">
      <div style={{ height: "60px" }} />
      <style>{`
        .svc-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
        }
        .brands-row {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 3rem;
          flex-wrap: wrap;
          margin-top: 2rem;
        }
        @media (max-width: 900px) {
          .svc-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 600px) {
          .svc-grid { grid-template-columns: 1fr; }
          .brands-row { gap: 1.5rem; }
        }
      `}</style>

      <section className="section">
        <div className="container">

          <div className="two-col" style={{ marginBottom: '5rem' }}>
            <div>
              <div className="label">Our Services</div>
              <h2 className="heading-xl">Complete Kitchen<br /><span className="orange-italic">Solutions</span></h2>
              <br />
              <p className="subtext">From initial concept to final installation, we offer a comprehensive suite of services to bring your dream kitchen to life. Every project receives our full attention and expertise.</p>
            </div>
            <img src="/images/kitchen-pantry.jpg" alt="Kitchen design" style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }} />
          </div>

          <div className="svc-grid">
            {services.map((svc, i) => (
              <div
                key={i}
                style={{
                  ...s.card,
                  borderColor: hoveredCard === i ? 'var(--orange)' : 'var(--border)',
                  transform: hoveredCard === i ? 'translateY(-2px)' : 'none',
                }}
                onMouseEnter={() => setHoveredCard(i)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div style={s.icon}>{svc.icon}</div>
                <h3 style={s.cardTitle}>{svc.title}</h3>
                <p className="body-p">{svc.text}</p>
                <ul style={s.list}>
                  {svc.bullets.map((b, j) => (
                    <li key={j} style={s.listItem}><span style={{ color: 'var(--orange)' }}>•</span> {b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section--sm section--dark" style={{ borderTop: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <div className="label" style={{ justifyContent: 'center' }}>Partner Brands</div>
            <h2 style={s.brandsH}>We Work With the Best</h2>
          </div>
          <div className="brands-row">
            {brands.map(b => <span key={b} style={s.brandName}>{b}</span>)}
          </div>
        </div>
      </section>

      <CtaBand
        title="Let's Discuss Your Project"
        text="Whether you have a clear vision or just the beginning of an idea, we're here to help."
        buttons={[
          { label: 'Book a Consultation', to: '/contact', variant: 'primary' },
          { label: 'View Our Work',       to: '/gallery', variant: 'outline' },
        ]}
      />
    </div>
  );
}

const s = {
  card: { background: 'var(--bg3)', border: '1px solid', padding: '2.5rem' },
  icon: { fontSize: '1.5rem', color: 'var(--orange)', marginBottom: '1.5rem' },
  cardTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: '1.4rem', fontWeight: 500, marginBottom: '0.75rem' },
  list: { listStyle: 'none' },
  listItem: { color: 'var(--text-muted)', fontSize: '0.82rem', padding: '0.2rem 0', display: 'flex', alignItems: 'center', gap: '0.6rem' },
  brandsH: { fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.6rem,3vw,2.5rem)', fontWeight: 400 },
  brandName: { fontFamily: "'Cormorant Garamond', serif", fontSize: '1.3rem', color: 'var(--text-dim)', fontStyle: 'normal' },
};
